import { useState } from 'react';
import {
  Button,
  Field,
  Flex,
  Input,
  Select,
  SelectContent,
  SelectTrigger,
  Text,
} from '@optiaxiom/react';

import { PageHeader } from '@frontend/components';
import { useNamespacedNavigate, useObjectTitle } from '@frontend/hooks';
import { AppPagePadding } from '@frontend/styles';

const productOptions = [
  { label: 'Experimentation', value: 'Experimentation' },
  { label: 'CMP', value: 'CMP' },
  { label: 'Opti-Graph', value: 'Opti-Graph' },
];

const instanceOptions = {
  Experimentation: [
    { label: 'Acme Corp Exp', value: 'Acme Corp Exp' },
    { label: 'Beta Inc Experiments', value: 'Beta Inc Experiments' },
    { label: 'Gamma Solutions Testing', value: 'Gamma Solutions Testing' },
  ],
  CMP: [
    { label: 'Acme Corp CMP', value: 'Acme Corp CMP' },
    { label: 'Delta Marketing Hub', value: 'Delta Marketing Hub' },
    { label: 'Echo Content Platform', value: 'Echo Content Platform' },
  ],
  'Opti-Graph': [
    { label: 'Acme Corp Analytics', value: 'Acme Corp Analytics' },
    { label: 'Foxtrot Data Lab', value: 'Foxtrot Data Lab' },
    { label: 'Golf Intelligence Hub', value: 'Golf Intelligence Hub' },
  ],
};

function RAGProvisionConfiguration() {
  useObjectTitle('RAG Provision');
  const navigate = useNamespacedNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    product: '',
    instance: '',
    description: '',
  });

  const handleProductChange = (product: string) => {
    setFormData({ ...formData, product, instance: '' });
  };

  const handleInstanceChange = (instance: string) => {
    setFormData({ ...formData, instance });
  };

  const handleDescriptionChange = (description: string) => {
    setFormData({ ...formData, description });
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      // TODO: Implement actual RAG configuration provisioning API call
      await new Promise(resolve => setTimeout(resolve, 2000)); // Simulate API call
      navigate('/rag');
    } catch (error) {
      console.error('Failed to provision RAG configuration:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/rag');
  };

  const isFormValid = formData.product && formData.instance && formData.description;
  const availableInstances = formData.product
    ? instanceOptions[formData.product as keyof typeof instanceOptions]
    : [];

  return (
    <Flex flexDirection="column" w="full" h="full">
      <Flex className={AppPagePadding} w="full">
        <PageHeader
          title="Provision RAG Configuration"
          headingText="Create a new RAG configuration for a product instance"
          goBackText="Back to RAG Settings"
          onClickGoBack={() => handleCancel()}
          // backButton={
          //   <Button appearance="subtle" icon={<IconArrowLeft />} onClick={handleCancel}>
          //     Back to RAG Settings
          //   </Button>
          // }
        />
      </Flex>

      <Flex flex="1" className={AppPagePadding} pt="0" flexDirection="column" gap="32">
        <Flex flexDirection="column" gap="24" w="384">
          <Field label="Product" required>
            <Select
              options={productOptions}
              value={formData.product}
              onValueChange={handleProductChange}
            >
              <SelectTrigger />
              <SelectContent />
            </Select>
          </Field>

          <Field label="Instance" required>
            <Select
              options={availableInstances}
              value={formData.instance}
              onValueChange={handleInstanceChange}
              disabled={!formData.product}
            >
              <SelectTrigger />
              <SelectContent />
            </Select>
          </Field>

          <Field label="Description" required>
            <Input
              placeholder="Enter a description for this RAG configuration"
              value={formData.description}
              onValueChange={handleDescriptionChange}
            />
          </Field>
        </Flex>

        <Flex flexDirection="row" gap="16">
          <Button
            appearance="primary"
            onClick={handleSubmit}
            loading={isLoading}
            disabled={!isFormValid}
          >
            Provision Configuration
          </Button>
          <Button appearance="default" onClick={handleCancel} disabled={isLoading}>
            Cancel
          </Button>
        </Flex>

        {formData.product && formData.instance && (
          <Flex flexDirection="column" gap="8" p="16" rounded="md">
            <Text fontSize="sm" fontWeight="500">
              Configuration Preview
            </Text>
            <Text fontSize="sm" color="fg.secondary">
              Product: {formData.product}
            </Text>
            <Text fontSize="sm" color="fg.secondary">
              Instance: {formData.instance}
            </Text>
            {formData.description && (
              <Text fontSize="sm" color="fg.secondary">
                Description: {formData.description}
              </Text>
            )}
          </Flex>
        )}
      </Flex>
    </Flex>
  );
}

export default RAGProvisionConfiguration;
